import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { fetchSession } from '../utils/authClient';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 20px;
  padding: 3rem;
  width: 100%;
  max-width: 800px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #00ff88;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const Subtitle = styled.p`
  text-align: center;
  color: #cccccc;
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: #00ff88;
  margin-bottom: 1rem;
  font-size: 1.2rem;
  border-bottom: 1px solid rgba(0, 255, 136, 0.3);
  padding-bottom: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  color: #cccccc;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  font-weight: 500;
  color: #ffffff;
`;

const Value = styled.span`
  color: #00ff88;
`;

const PhoneNumberDisplay = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 2px solid #00ff88;
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  margin: 2rem 0;
`;

const PhoneNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #00ff88;
  margin-bottom: 0.5rem;
`;

const PhoneStatus = styled.div`
  color: #00ff88;
  font-size: 1.1rem;
  margin-bottom: 1rem;
`;

const StatusBadge = styled.span`
  background: #00ff88;
  color: #000000;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  background: ${props => props.primary ? 'linear-gradient(45deg, #00ff88, #00cc6a)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.primary ? '#000000' : '#ffffff'};
  padding: 1rem 2rem;
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'};
  border-radius: 10px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 150px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.primary ? '0 6px 20px rgba(0, 255, 136, 0.4)' : '0 6px 20px rgba(255, 255, 255, 0.2)'};
  }
`;

const UsageSection = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid #00d4ff;
  border-radius: 15px;
  padding: 1.5rem;
  margin: 2rem 0;
`;

const UsageTitle = styled.h3`
  color: #00d4ff;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const UsageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const UsageCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 1rem;
  text-align: center;
`;

const UsageValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #00d4ff;
  margin-bottom: 0.25rem;
`;

const UsageLabel = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
`;

const InfoBox = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid #ffc107;
  border-radius: 10px;
  padding: 1rem;
  margin: 2rem 0;
  color: #ffc107;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const InmateProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [inmateData, setInmateData] = useState(null);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);

  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const load = async () => {
      const session = await fetchSession();
      if (session.network || session.error) {
        setAuthError(session.message || 'Unable to load account.');
        setLoading(false);
        return;
      }
      if (session.status === 401) {
        navigate('/signin', { state: { from: '/inmate-profile' } });
        return;
      }
      if (session.status === 403) {
        setAuthError(session.message || 'This account cannot view that profile yet.');
        setLoading(false);
        return;
      }

      const { inmateData: stateInmateData, selectedNumber: stateSelectedNumber } = location.state || {};
      if (stateInmateData && stateSelectedNumber) {
        setInmateData(stateInmateData);
        setSelectedNumber(stateSelectedNumber);
        fetchBalance(stateInmateData.inmateId);
        setLoading(false);
        return;
      }
      setLoading(false);
    };
    load();
  }, [location.state, navigate]);

  const fetchBalance = async (inmateId) => {
    try {
      const res = await fetch(`/api/inmate/${inmateId}`, { credentials: 'include' });
      const data = await res.json();
      setBalance(data.balance);
    } catch (err) {
      setBalance(null);
    }
  };

  const formatPhoneNumber = (number) => {
    const cleaned = number.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return number;
  };

  const handleLoadMoney = () => {
    navigate('/load-money', {
      state: { inmateData, selectedNumber }
    });
  };

  const handleManageContacts = () => {
    navigate('/connect-to-inmate', {
      state: { inmateData, selectedNumber }
    });
  };

  const handleEditProfile = () => {
    // Navigate to edit profile page (to be implemented)
    console.log('Edit profile functionality to be implemented');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (authError) {
    return (
      <Container>
        <Card>
          <Title>Unable to load profile</Title>
          <Subtitle>{authError}</Subtitle>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Card>
          <Title>Loading Profile...</Title>
        </Card>
      </Container>
    );
  }

  if (!inmateData || !selectedNumber) {
    return (
      <Container>
        <Card>
          <Title>Profile Not Found</Title>
          <Subtitle>Unable to load inmate profile</Subtitle>
          <ActionButtons>
            <Button primary onClick={() => navigate('/inmate-signup')}>
              Create New Profile
            </Button>
            <Button onClick={handleGoToDashboard}>
              Go to Dashboard
            </Button>
          </ActionButtons>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Title>Inmate Profile</Title>
        <Subtitle>Manage {inmateData.inmateName}'s Skylink account</Subtitle>

        <PhoneNumberDisplay>
          <PhoneNumber>{formatPhoneNumber(selectedNumber.phoneNumber)}</PhoneNumber>
          <PhoneStatus>
            <StatusBadge>Active</StatusBadge>
          </PhoneStatus>
          <div style={{ color: '#cccccc', fontSize: '0.9rem' }}>
            Ready to receive calls
          </div>
        </PhoneNumberDisplay>

        <ProfileGrid>
          <ProfileSection>
            <SectionTitle>Inmate Information</SectionTitle>
            <InfoRow>
              <Label>Name:</Label>
              <Value>{inmateData.inmateName}</Value>
            </InfoRow>
            <InfoRow>
              <Label>ID Number:</Label>
              <Value>{inmateData.inmateId}</Value>
            </InfoRow>
            <InfoRow>
              <Label>Facility:</Label>
              <Value>{inmateData.facilityName}</Value>
            </InfoRow>
            <InfoRow>
              <Label>State:</Label>
              <Value>{inmateData.state}</Value>
            </InfoRow>
            <InfoRow>
              <Label>Relationship:</Label>
              <Value>{inmateData.relationship}</Value>
            </InfoRow>
          </ProfileSection>

          <ProfileSection>
            <SectionTitle>Account Information</SectionTitle>
            <InfoRow>
              <Label>Username:</Label>
              <Value>{inmateData.username}</Value>
            </InfoRow>
            <InfoRow>
              <Label>Account Status:</Label>
              <Value>Active</Value>
            </InfoRow>
            <InfoRow>
              <Label>Created:</Label>
              <Value>{new Date().toLocaleDateString()}</Value>
            </InfoRow>
            <InfoRow>
              <Label>Monthly Fee:</Label>
              <Value>$9.99</Value>
            </InfoRow>
            <InfoRow>
              <Label>Next Billing:</Label>
              <Value>{new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</Value>
            </InfoRow>
          </ProfileSection>
        </ProfileGrid>

        <UsageSection>
          <UsageTitle>Account Usage</UsageTitle>
          <UsageGrid>
            <UsageCard>
              <UsageValue>{balance !== null ? `$${balance.toFixed(2)}` : '...'}</UsageValue>
              <UsageLabel>Available Balance</UsageLabel>
            </UsageCard>
            <UsageCard>
              <UsageValue>120</UsageValue>
              <UsageLabel>Minutes Used This Month</UsageLabel>
            </UsageCard>
            <UsageCard>
              <UsageValue>15</UsageValue>
              <UsageLabel>Total Calls</UsageLabel>
            </UsageCard>
            <UsageCard>
              <UsageValue>3</UsageValue>
              <UsageLabel>Active Contacts</UsageLabel>
            </UsageCard>
          </UsageGrid>
        </UsageSection>

        <InfoBox>
          <strong>Important:</strong> Share the phone number with the inmate's facility so they can 
          receive calls. Account top-ups are currently unavailable.
        </InfoBox>

        <ActionButtons>
          <Button primary onClick={handleLoadMoney}>
            Account Top-ups (Unavailable)
          </Button>
          <Button onClick={handleManageContacts}>
            Manage Contacts
          </Button>
          <Button onClick={handleEditProfile}>
            Edit Profile
          </Button>
          <Button onClick={handleGoToDashboard}>
            Dashboard
          </Button>
        </ActionButtons>
      </Card>
    </Container>
  );
};

export default InmateProfile;
